/**
 * apps/web/src/api/auth.ts
 *
 * Auth boundary. Maps to /v1/auth (apps/api/src/routes/auth.routes.ts):
 *   POST /auth/login              → LoginResult (sets httpOnly session cookie)
 *   POST /auth/logout             → clears the cookie
 *   GET  /auth/me                 → AuthSession for the current cookie
 *
 *   Passwordless (hybrid magic-link) flow:
 *   POST /auth/magic-link         → always 200 (no account-probing); emails a link
 *   POST /auth/magic-link/verify  → verify token, set cookie, return session + plan
 *   POST /auth/set-password       → commit a provisional account (requires session)
 */

import { apiGet, apiPost } from '../lib/api';
import type {
  AuthSession,
  LoginResult,
  MagicLinkVerifyResult,
  OnboardingSegment,
  User,
} from './types';

export interface LoginPayload {
  readonly email: string;
  readonly password: string;
  readonly workspaceSlug?: string;
}

export function login(payload: LoginPayload): Promise<LoginResult> {
  return apiPost<LoginResult>('/auth/login', payload);
}

export function logout(): Promise<{ loggedOut: boolean }> {
  return apiPost<{ loggedOut: boolean }>('/auth/logout');
}

export function getProfile(): Promise<AuthSession> {
  return apiGet<AuthSession>('/auth/me');
}

// ── Passwordless start ────────────────────────────────────────────────────────

export interface MagicLinkRequestPayload {
  readonly email: string;
  readonly firstName?: string;
  readonly lastName?: string;
  readonly workspaceName?: string;
  readonly segment?: OnboardingSegment;
}

/**
 * Request a sign-in link. The API always returns 200 with a uniform message so
 * callers cannot probe whether an email is registered.
 */
export function requestMagicLink(
  payload: MagicLinkRequestPayload,
): Promise<{ accepted: boolean }> {
  return apiPost<{ accepted: boolean }>('/auth/magic-link', payload);
}

export interface MagicLinkVerifyPayload {
  readonly token: string;
  readonly segment?: OnboardingSegment;
}

/**
 * Verify the single-use link token. On success the server sets the httpOnly
 * session cookie and returns the full session plus the segment-personalised
 * onboarding plan.
 */
export function verifyMagicLink(
  payload: MagicLinkVerifyPayload,
): Promise<MagicLinkVerifyResult> {
  return apiPost<MagicLinkVerifyResult>('/auth/magic-link/verify', payload);
}

/**
 * Commit a provisional account by setting a real password (the final onboarding
 * step). Requires the magic-link session cookie.
 */
export function setPassword(password: string): Promise<{ committed: boolean; user: User }> {
  return apiPost<{ committed: boolean; user: User }>('/auth/set-password', { password });
}
